import React, { lazy, Suspense, useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { Redirect, Route, Switch, useLocation } from "react-router-dom";
import {
  getToken,
  iotViewCounter,
  tokenTagRequest,
  constructHeaders,
} from "./api";
import { handleTokenTagResponse } from "./utils/tokenTag";
import styles from "./App.module.scss";
import UploadSelection from "./components/Modal/UploadSelection";
import ShippingAddress from "./components/ShippingAddress";
import SpinConnectDownload from "./components/SpinConnectDownload";
import { store } from "./store";

// Components
import Spinner from "./components/Spinner";
import routes from "./routes";
import ProfileReminderModal from "./components/Modal/ProfileReminderModal";
import SubmittedPerfectly from "./components/Modal/BusinessProfileModal/SubmittedPerfectly";

// Utilities
import {
  shouldShowReminder,
  setupReminderListeners,
  markReminderShown,
} from "./utils/profileReminder";

import { setCookie } from "./utils/cookies";
import { getUserToken, getUserInfo } from "./utils/localStorage";


const NavBar = lazy(() => import("./components/NavBar"));
const ErrorRequestModal = lazy(() =>
  import("./components/Modal/ErrorRequestModal")
);
const ReachedLimitModal = lazy(() =>
  import("./components/Modal/SubscriptionModals/ReachedLimitModal")
);
const ProfileModal = lazy(() => import("./components/Modal/ProfileModal"));

// Public routes
const MainHome = lazy(() => import("./pages/public/MainHome"));
const Login = lazy(() => import("./pages/public/Login"));
const Signup = lazy(() => import("./pages/public/Signup"));
const Footer = lazy(() => import("./components/Footer"));
const HeaderPreAuth = lazy(() => import("./components/Header/HeaderPreAuth"));
const ResetPassword = lazy(() => import("./pages/public/ResetPassword"));
const TeamMemberWelcome = lazy(() => import("./pages/public/TeamMemberSignup/Welcome"));
const TeamMemberSignupStep1 = lazy(() => import("./pages/public/TeamMemberSignup/TeamMemberSignupStep1"));
const TeamMemberSignupStep2 = lazy(() => import("./pages/public/TeamMemberSignup/TeamMemberSignupStep2"));
const SnapTryOn = lazy(() => import("./pages/public/SnapTryOn"));
const TermsUsers = lazy(() => import("./pages/public/TermsUsers"));
const TermsBusiness = lazy(() => import("./pages/public/TermsBusiness"));
const TermsConditions = lazy(() => import("./pages/public/TermsConditions"));
const PrivacyPolicy = lazy(() => import("./pages/public/PrivacyPolicy"));
const CookiePolicie = lazy(() => import("./pages/public/CookiePolicie"));
const TokenTagInfo = lazy(() => import("./pages/public/TokenTagInfo"));

// Private routes
const Profile = lazy(() => import("./pages/private/Profile"));
const Following = lazy(() => import("./pages/private/Following"));
const Spins = lazy(() => import("./pages/private/Spins"));
const ProductInfo = lazy(() => import("./pages/private/ProductInfo"));
const ConfirmPasson = lazy(() => import("./pages/private/ConfirmPasson"));
const Dialog = lazy(() => import("./pages/private/Chat/Dialog"));
const Dms = lazy(() => import("./pages/private/Chat/Dms"));
const Vouchers = lazy(() => import("./pages/private/Vouchers"));
const Gallery = lazy(() => import("./pages/private/Gallery"));
const GalleryListView = lazy(() => import("./pages/private/GalleryListView"));
const Subscription = lazy(() => import("./pages/private/Subscription"));
const IoT = lazy(() => import("./pages/private/IoT"));

const App = () => {
  const store = useSelector((state) => state.authReducer);
  const location = useLocation();
  const { userInfo } = useSelector((state) => state.authReducer);
  const { scrollTop } = useSelector((state) => state.profileReducer);
  const [isUploadSelectionOpened, setIsUploadSelectionOpened] = useState(false);
  const [showProfileReminder, setShowProfileReminder] = useState(false);
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [appInitialized, setAppInitialized] = useState(false);
  const dispatch = useDispatch();

  // Wait a moment before starting reminder checks to prevent immediate popups
  useEffect(() => {
    const timer = setTimeout(() => {
      setAppInitialized(true);
    }, 1000);

    return () => clearTimeout(timer);
  }, []);

  // Cross-subdomain Synchronizer: Make cookies the absolute source of truth.
  // Because localStorage is isolated per subdomain, field.spin.tf and spin.tf can get out of sync.
  useEffect(() => {
    try {
      const cookieToken = getCookie("access_token");
      const cookieUserInfo = getCookie("user_info");

      if (cookieToken) {
        localStorage.setItem("access_token", cookieToken);
      } else {
        // Fallback for legacy host-only cookies
        const localToken = localStorage.getItem("access_token");
        if (localToken) {
          setCookie("access_token", localToken, 30);
        }
      }

      if (cookieUserInfo) {
        // The cookie might be URI encoded
        let decoded = cookieUserInfo;
        try {
          decoded = decodeURIComponent(cookieUserInfo);
        } catch(e) {}
        localStorage.setItem("user_info", decoded);
      } else {
        const localUserInfo = localStorage.getItem("user_info");
        if (localUserInfo) {
          setCookie("user_info", encodeURIComponent(localUserInfo), 30);
        }
      }
    } catch (e) {
      console.warn("Failed to sync auth cookies", e);
    }
  }, []);

  // Track if user was on /register or /enter route for back arrow visibility
  useEffect(() => {
    if (typeof window === "undefined") return;
    const CAME_FROM_REGISTER_KEY = "came_from_register";

    if (
      location.pathname === routes.register ||
      location.pathname === routes.prelogin
    ) {
      localStorage.setItem(CAME_FROM_REGISTER_KEY, "true");
    } else {
      localStorage.setItem(CAME_FROM_REGISTER_KEY, "false");
    }
  }, [location.pathname]);

  const closeUploadSelection = () => {
    const cards = document.querySelectorAll('[class^="Nav"]');

    if (cards.length > 0) {
      cards.forEach((card) => {
        card.style.zIndex = "1";
      });
    }
    window.scrollTo({ top: scrollTop });
    setIsUploadSelectionOpened(false);
    document.body.style.overflow = "initial";
  };

  // Handle new user profile initialization
  useEffect(() => {
    if (!store.isAuthenticated || !userInfo) return;

    // Check if we should show the reminder immediately on page load
    if (shouldShowReminder(userInfo)) {
      setShowProfileReminder(true);
    }
  }, [store.isAuthenticated, userInfo]);

  // Set up interval and event listeners to check for reminder status
  useEffect(() => {
    if (!appInitialized || !store.isAuthenticated || !userInfo) return;

    // Set up all the event listeners and interval
    return setupReminderListeners(
      userInfo,
      () => setShowProfileReminder(true) // Show reminder function
    );
  }, [appInitialized, store.isAuthenticated, userInfo]);

  const handleReminderClose = () => {
    setShowProfileReminder(false);
    if (userInfo?.uid) {
      markReminderShown(userInfo.uid);
    }
  };

  const handleEditProfile = () => {
    setShowProfileReminder(false);
    setShowProfileModal(true);
    if (userInfo?.uid) {
      markReminderShown(userInfo.uid);
      localStorage.setItem("should_open_edit_profile", "true");
    }
  };

  const handleProfileModalClose = () => {
    setShowProfileModal(false);
  };

  // Handle pending IoT URL redirects after authentication
  useEffect(() => {
    if (store.isAuthenticated) {
      const pendingIotUrl = localStorage.getItem("pending_iot_url");

      if (pendingIotUrl) {
        // Clear the pending IoT URL
        localStorage.removeItem("pending_iot_url");

        // Redirect to the IoT URL
        setTimeout(() => {
          window.location.href = pendingIotUrl;
        }, 1000); // 1 second delay to ensure authentication is complete
      }
    }
  }, [store.isAuthenticated]);

  // We allow the Welcome and Step 2 pages even if authenticated. 
  // Welcome page triggers auto-logout. Step 2 is the final mandatory setup after Step 1 login.
  const isSignupFlow = [
    routes.teamMemberWelcome,
    routes.TeamMemberSignupStep2
  ].includes(location.pathname);

  if (
    (!store.isAuthenticated || isSignupFlow) &&
    (location.pathname === routes.prelogin ||
      location.pathname === routes.register ||
      location.pathname === routes.resetPassword ||
      location.pathname === routes.teamMemberWelcome ||
      location.pathname === routes.TeamMemberSignupStep1 ||
      location.pathname === routes.TeamMemberSignupStep2)
  ) {
    return (
      <Suspense fallback={null}>
        <ErrorRequestModal />
        <ReachedLimitModal />
        <div className={styles.App}>
          <Switch>
            <Route
              exact
              path={[routes.prelogin, routes.register, routes.resetPassword, routes.teamMemberWelcome, routes.TeamMemberSignupStep1, routes.TeamMemberSignupStep2]}
            >
              <main className={styles.mainLogin}>
                <Switch>
                  <Route exact path={routes.prelogin} component={Login} />
                  <Route exact path={routes.register} component={Signup} />
                  <Route
                    path={routes.resetPassword}
                    component={ResetPassword}
                  />
                  <Route
                    path={routes.TeamMemberSignupStep1}
                    component={TeamMemberSignupStep1}
                  />
                  <Route
                    path={routes.TeamMemberSignupStep2}
                    component={TeamMemberSignupStep2}
                  />
                  <Route
                    path={routes.teamMemberWelcome}
                    component={TeamMemberWelcome}
                  />
                </Switch>
              </main>
            </Route>
            <Route>
              <main>
                <Switch>
                  <Redirect exact from="/wearable/:uid" to="/object/:uid" />
                  <Redirect exact from="/artefact/:uid" to="/object/:uid" />
                  <Redirect exact from="/wearable/:uid/ar/tryon" to="/object/:uid/ar/tryon" />
                  <Route
                    exact
                    path={[
                      routes.viewAsProfile,
                      `${routes.viewAsProfile}/:activeTab`,
                    ]}
                    render={(props) => {
                      return (
                        <Profile
                          isPublic
                          {...props}
                          setIsUploadSelectionOpened={
                            setIsUploadSelectionOpened
                          }
                        />
                      );
                    }}
                  />
                  <Route
                    exact
                    path={[routes.spins, routes.viewAsSpins]}
                    render={(props) => <Spins isPublic {...props} />}
                  />
                  <Route
                    exact
                    path={[routes.spinconnect]}
                    render={(props) => (
                      <SpinConnectDownload isPublic {...props} />
                    )}
                  />
                  <Route
                    exact
                    path={[
                      routes.tokenizedProduct,
                      routes.wearableProduct,
                      routes.artefactProduct,
                      routes.architectureProduct,
                      routes.spaceObject,
                      routes.checkoutProduct,
                    ]}
                    render={(props) => <ProductInfo isPublic {...props} />}
                  />
                  <Route
                    exact
                    path="/iot/:slug"
                    render={({ match }) => {
                      const { slug } = match.params;

                      // Execute synchronously before redirect
                      (async () => {
                        try {
                          const res = await getToken(slug);

                          if (res?.response?.token) {
                            const token = res.response.token;

                            const viewRes = await iotViewCounter(token);

                            // Check if user is already authenticated
                            if (store.isAuthenticated) {
                              // Get product info to get game ID
                              const myHeaders = constructHeaders();
                              const productRes = await fetch(
                                `${process.env.REACT_APP_BASE_URL}/content/uniqueProduct/${token}`,
                                {
                                  method: "GET",
                                  headers: myHeaders,
                                }
                              );

                              if (productRes.ok) {
                                const productInfo = await productRes.json();

                                if (productInfo?.token_tag_game?.id) {
                                  const gameId = productInfo.token_tag_game.id;

                                  const tagRes = await tokenTagRequest(
                                    token,
                                    gameId
                                  );
                                  if (tagRes?.response?.success) {
                                    // Store success data for the modal
                                    const successData = {
                                      targetPlayerName:
                                        tagRes.response.target_player_name ||
                                        "Robert Fox",
                                      pointsTransferred:
                                        tagRes.response.points_transferred ||
                                        200,
                                      message: tagRes.response.message,
                                    };
                                    localStorage.setItem(
                                      "tokenTagSuccessData",
                                      JSON.stringify(successData)
                                    );
                                  } else {
                                    console.log(
                                      "Immediate token tag request failed:",
                                      tagRes?.response?.reason || tagRes
                                    );
                                  }
                                } else {
                                  console.log(
                                    "No active token tag game found for this product"
                                  );
                                }
                              } else {
                                console.error(
                                  "Failed to get product info for immediate token tag"
                                );
                              }

                              // Redirect to the product page
                              window.location.href = `/item/${slug}`;
                            } else {
                              // User is not authenticated, store the IoT URL for later
                              localStorage.setItem(
                                "pending_iot_url",
                                `/iot/${slug}`
                              );

                              // Redirect to login
                              // Note: iot_url flag will be set when user returns from login
                              setTimeout(() => {
                                window.location.href = routes.prelogin;
                              }, 5000); // 5 second delay
                            }
                          } else {
                            console.error(
                              "Failed to get token for slug:",
                              slug,
                              res
                            );
                          }
                        } catch (error) {
                          console.error("Error processing IoT request:", error);
                        }
                      })();

                      return null;
                    }}
                  />
                  <Route
                    exact
                    path={[
                      `${routes.wearableProduct}/ar${routes.tryon}`,
                      `${routes.spaceObject}/ar${routes.tryon}`,
                    ]}
                    component={SnapTryOn}
                  />
                  <Route
                    exact
                    path={[
                      routes.tokenizedProduct,
                      routes.wearableProduct,
                      routes.artefactProduct,
                      routes.architectureProduct,
                      routes.spaceObject,
                      routes.checkoutProduct,
                    ]}
                    render={(props) => <ProductInfo isPublic {...props} />}
                  />
                  <Route exact path={routes.iot} component={IoT} />
                  <Route
                    exact
                    path="/iot/:slug"
                    render={({ match }) => {
                      const { slug } = match.params;
                      getToken(slug).then((res) => {
                        const token = res.response.token;
                        iotViewCounter(token);
                      });
                      // Use sessionStorage instead of localStorage to ensure it's cleared when tab closes
                      // and add a timestamp to make it more reliable
                      sessionStorage.setItem(
                        "iot_url",
                        JSON.stringify({
                          value: true,
                          timestamp: Date.now(),
                          slug: slug,
                        })
                      );
                      return <Redirect to={`/item/${slug}`} />;
                    }}
                  />
                  <Route
                    exact
                    path={[
                      `${routes.wearableProduct}/ar${routes.tryon}`,
                      `${routes.spaceObject}/ar${routes.tryon}`,
                    ]}
                    component={SnapTryOn}
                  />
                  <Route path={routes.subscription} component={Subscription} />
                  <Route
                    exact
                    path={routes.termsConditions}
                    component={TermsConditions}
                  />
                  <Route
                    exact
                    path={routes.termsUsers}
                    component={TermsUsers}
                  />
                  <Route
                    exact
                    path={routes.termsBusiness}
                    component={TermsBusiness}
                  />
                  <Route
                    exact
                    path={routes.termsCookies}
                    component={CookiePolicie}
                  />
                  <Route
                    exact
                    path={routes.termsPrivacy}
                    component={PrivacyPolicy}
                  />
                  <Redirect to={routes.prelogin} />
                </Switch>
              </main>
            </Route>
          </Switch>
        </div>
        <Spinner />
      </Suspense>
    );
  }

  // ! Private routes
  const isFieldSubdomain = typeof window !== "undefined" && window.location.hostname.startsWith("field.");

  if (isFieldSubdomain) {
    if (!store.isAuthenticated) {
      window.location.href = `http://${window.location.hostname.replace("field.", "")}/enter`;
      return <Spinner />;
    }

    return (
      <Suspense fallback={null}>
        <ErrorRequestModal />
        <ReachedLimitModal />
        <div className={`${styles.App} ${styles.isAuthenticated}`}>
          <main>
            <Switch>
              <Route exact path={[routes.chat, routes.dms, routes.dmsActive, routes.hubs, routes.hubsActive]} component={Dms} />
              <Route exact path={routes.dialog} component={Dialog} />
              <Redirect to={routes.chat} />
            </Switch>
            <NavBar setIsUploadSelectionOpened={setIsUploadSelectionOpened} />
            {isUploadSelectionOpened && (
              <UploadSelection
                role={userInfo?.business_role}
                onClose={closeUploadSelection}
              />
            )}
          </main>
        </div>
        <Spinner />
      </Suspense>
    );
  }

  return (
    <Suspense fallback={null}>
      <ErrorRequestModal />
      <ReachedLimitModal />
      <div className={`${styles.App} ${styles.isAuthenticated}`}>
        <main>
          <Switch>
            <Redirect exact from="/wearable/:uid" to="/object/:uid" />
            <Redirect exact from="/artefact/:uid" to="/object/:uid" />
            <Redirect exact from="/wearable/:uid/ar/tryon" to="/object/:uid/ar/tryon" />
            <Route path={routes.subscription} component={Subscription} />
            <Route
              exact
              path={[routes.vouchers, routes.viewAsVouchers]}
              component={Vouchers}
            />
            <Route exact path={routes.gallery} component={Gallery} />
            <Route
              exact
              path={routes.galleryListView}
              component={GalleryListView}
            />
            <Route exact path={routes.tokenTagInfo} component={TokenTagInfo} />
            <Route
              exact
              path={[
                routes.profile,
                routes.viewAsProfile,
                `${routes.viewAsProfile}/:activeTab`,
                routes.giveback,
              ]}
              render={(props) => (
                <Profile
                  {...props}
                  setIsUploadSelectionOpened={setIsUploadSelectionOpened}
                />
              )}
            />

            <Route
              exact
              path={[routes.shippingAdress]}
              render={(props) => (
                <ShippingAddress
                  isPublic
                  {...props}
                  setIsUploadSelectionOpened={setIsUploadSelectionOpened}
                />
              )}
            />

            <Route
              exact
              path={[routes.os]}
              render={(props) => <SpinConnectDownload isPublic {...props} />}
            />
            <Route
              exact
              path={[routes.following, routes.viewAsFollowing]}
              render={(props) => <Following {...props} />}
            />
            <Route
              exact
              path={[routes.spins, routes.viewAsSpins]}
              render={(props) => <Spins {...props} />}
              component={Spins}
            />
            <Route
              exact
              path={[
                routes.tokenizedProduct,
                routes.wearableProduct,
                routes.artefactProduct,
                routes.architectureProduct,
                routes.spaceObject,
                routes.checkoutProduct,
              ]}
              render={(props) => <ProductInfo isPublic={false} {...props} />}
            />
            <Route exact path={routes.iot} component={IoT} />
            <Route
              exact
              path="/iot/:slug"
              render={({ match }) => {
                const { slug } = match.params;
                getToken(slug)
                  .then((res) => {
                    if (res?.response?.token) {
                      const token = res.response.token;
                      iotViewCounter(token);

                      // Get product info to get game ID
                      const myHeaders = constructHeaders();
                      fetch(
                        `${process.env.REACT_APP_BASE_URL}/content/uniqueProduct/${token}`,
                        {
                          method: "GET",
                          headers: myHeaders,
                        }
                      )
                        .then((productRes) => {
                          if (productRes.ok) {
                            return productRes.json();
                          }
                          return null;
                        })
                        .then((productInfo) => {
                          if (productInfo?.token_tag_game?.id) {
                            const gameId = productInfo.token_tag_game.id;
                            // Send token tag request
                            tokenTagRequest(token, gameId)
                              .then((tagRes) => {
                                handleTokenTagResponse(tagRes);
                              })
                              .catch((error) => {
                                handleTokenTagResponse(null, error);
                              });
                          } else {
                            console.log(
                              "No active token tag game found for this product"
                            );
                          }
                        })
                        .catch((error) => {
                          console.error("Error getting product info:", error);
                        });
                    } else {
                      console.error("Failed to get token for slug:", slug, res);
                    }
                  })
                  .catch((error) => {
                    console.error("Error getting token:", error);
                  });
                // Use sessionStorage instead of localStorage to ensure it's cleared when tab closes
                // and add a timestamp to make it more reliable
                sessionStorage.setItem(
                  "iot_url",
                  JSON.stringify({
                    value: true,
                    timestamp: Date.now(),
                    slug: slug,
                  })
                );
                return <Redirect to={`/item/${slug}`} />;
              }}
            />
            <Route exact path={routes.iot} component={IoT} />
            <Route
              exact
              path={[
                `${routes.wearableProduct}/ar${routes.tryon}`,
                `${routes.spaceObject}/ar${routes.tryon}`,
              ]}
              component={SnapTryOn}
            />

            <Route path={routes.confirmPasson} component={ConfirmPasson} />
            <Route
              exact
              path={routes.termsConditions}
              component={TermsConditions}
            />
            <Route exact path={routes.termsUsers} component={TermsUsers} />
            <Route
              exact
              path={routes.termsBusiness}
              component={TermsBusiness}
            />
            <Route
              exact
              path={routes.termsCookies}
              component={CookiePolicie}
            />
            <Route exact path={routes.termsPrivacy} component={PrivacyPolicy} />
            <Route exact path={routes.profileModal} component={ProfileModal} />
            <Route
              exact
              path={routes.submittedPerfectly}
              component={SubmittedPerfectly}
            />
            <Redirect
              to={userInfo ? `/profile/${userInfo.uid}` : routes.prelogin}
            />
          </Switch>
          <NavBar setIsUploadSelectionOpened={setIsUploadSelectionOpened} />
          {isUploadSelectionOpened && (
            <UploadSelection
              role={userInfo?.business_role}
              onClose={closeUploadSelection}
            />
          )}

          <ProfileReminderModal
            isOpen={showProfileReminder}
            onClose={handleReminderClose}
            onEditProfile={handleEditProfile}
          />

          {showProfileModal && (
            <ProfileModal onClose={handleProfileModalClose} />
          )}
        </main>
      </div>
      <Spinner />
    </Suspense>
  );
};

export default App;
